import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { HashLoader } from "react-spinners";
import Header from "../../components/header";
import Post from "../../components/post";
import { search } from "../../functions/user";
import { searchPosts, searchVideos } from "../../functions/post";
import "./style.css";

export default function Search({ setVisible }) {
  const { user } = useSelector((state) => ({ ...state }));
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [people, setPeople] = useState([]);
  const [posts, setPosts] = useState([]);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    if (query) {
      performSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const performSearch = async () => {
    setLoading(true);
    try {
      const [peopleRes, postsRes, videosRes] = await Promise.all([
        search(query, user.token),
        searchPosts(query, user.token),
        searchVideos(query, user.token),
      ]);
      setPeople(Array.isArray(peopleRes) ? peopleRes : []);
      setPosts(Array.isArray(postsRes) ? postsRes : []);
      setVideos(Array.isArray(videosRes) ? videosRes : []);
    } catch (error) {
      console.error("Search error:", error);
    }
    setLoading(false);
  };

  const tabs = [
    { id: "all", label: "All", count: people.length + posts.length + videos.length },
    { id: "people", label: "People", count: people.length },
    { id: "posts", label: "Posts", count: posts.length },
    { id: "videos", label: "Videos", count: videos.length },
  ];

  const renderPeopleResults = () => {
    if (people.length === 0) {
      return <div className="no_results">No people found for "{query}"</div>;
    }
    return (
      <div className="search_people_list">
        {people.map((person) => (
          <Link
            to={`/profile/${person.username}`}
            key={person._id}
            className="search_person_card"
          >
            <img
              src={person.picture}
              alt={`${person.first_name} ${person.last_name}`}
              className="search_person_img"
            />
            <div className="search_person_info">
              <span className="search_person_name">
                {person.first_name} {person.last_name}
              </span>
              {person.details?.workplace && (
                <span className="search_person_detail">
                  Works at {person.details.workplace}
                </span>
              )}
              {person.mutualFriends > 0 && (
                <span className="search_person_mutual">
                  {person.mutualFriends} mutual friend{person.mutualFriends > 1 ? "s" : ""}
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    );
  };

  const renderPostsResults = () => {
    if (posts.length === 0) {
      return <div className="no_results">No posts found for "{query}"</div>;
    }
    return (
      <div className="search_posts_list">
        {posts.map((post) => (
          <Post key={post._id} post={post} user={user} />
        ))}
      </div>
    );
  };

  const renderVideosResults = () => {
    if (videos.length === 0) {
      return <div className="no_results">No videos found for "{query}"</div>;
    }
    return (
      <div className="search_videos_grid">
        {videos.map((video) => (
          <div key={video._id} className="search_video_card">
            <Link
              to={`/profile/${video.user?.username}`}
              className="search_video_user"
            >
              <img src={video.user?.picture} alt="" className="search_video_user_img" />
              <div className="search_video_user_info">
                <span className="search_video_user_name">
                  {video.user?.first_name} {video.user?.last_name}
                </span>
              </div>
            </Link>
            {video.images?.map((img, idx) => {
              const isVideo = img.url?.includes("/video/") ||
                            img.url?.match(/\.(mp4|webm|ogg|mov)$/i);
              if (isVideo) {
                return (
                  <video
                    key={idx}
                    className="search_video_player"
                    controls
                    src={img.url}
                  />
                );
              }
              return null;
            })}
            {video.text && <p className="search_video_text">{video.text}</p>}
          </div>
        ))}
      </div>
    );
  };

  const renderAllResults = () => {
    return (
      <>
        {people.length > 0 && (
          <div className="search_section">
            <div className="search_section_header">
              <h3>People</h3>
              <button onClick={() => setActiveTab("people")}>See all</button>
            </div>
            <div className="search_people_list horizontal">
              {people.slice(0, 5).map((person) => (
                <Link
                  to={`/profile/${person.username}`}
                  key={person._id}
                  className="search_person_card_small"
                >
                  <img
                    src={person.picture}
                    alt={`${person.first_name} ${person.last_name}`}
                    className="search_person_img_small"
                  />
                  <span className="search_person_name_small">
                    {person.first_name} {person.last_name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
        {posts.length > 0 && (
          <div className="search_section">
            <div className="search_section_header">
              <h3>Posts</h3>
              <button onClick={() => setActiveTab("posts")}>See all</button>
            </div>
            <div className="search_posts_preview">
              {posts.slice(0, 3).map((post) => (
                <Post key={post._id} post={post} user={user} />
              ))}
            </div>
          </div>
        )}
        {videos.length > 0 && (
          <div className="search_section">
            <div className="search_section_header">
              <h3>Videos</h3>
              <button onClick={() => setActiveTab("videos")}>See all</button>
            </div>
            <div className="search_videos_grid preview">
              {videos.slice(0, 4).map((video) => (
                <div key={video._id} className="search_video_card_small">
                  {video.images?.map((img, idx) => {
                    const isVideo = img.url?.includes("/video/") ||
                                  img.url?.match(/\.(mp4|webm|ogg|mov)$/i);
                    if (isVideo) {
                      return (
                        <video
                          key={idx}
                          className="search_video_thumbnail"
                          src={img.url}
                        />
                      );
                    }
                    return null;
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
        {people.length === 0 && posts.length === 0 && videos.length === 0 && (
          <div className="no_results">No results found for "{query}"</div>
        )}
      </>
    );
  };

  return (
    <div className="search_page">
      <Header page="search" />
      <div className="search_page_container">
        <div className="search_sidebar">
          <h2>Search Results</h2>
          {query && <p className="search_query">Results for: "{query}"</p>}
          <div className="search_filters">
            <h4>Filters</h4>
            <div className="search_tabs_vertical">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`search_tab ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span>{tab.label}</span>
                  {tab.count > 0 && <span className="tab_count">{tab.count}</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="search_main">
          {!query ? (
            <div className="search_empty">
              <i className="search_icon_large"></i>
              <h3>Search for something</h3>
              <p>Use the search bar above to find people, posts, and videos</p>
            </div>
          ) : loading ? (
            <div className="search_loading">
              <HashLoader color="#1876f2" />
            </div>
          ) : (
            <div className="search_results">
              {activeTab === "all" && renderAllResults()}
              {activeTab === "people" && renderPeopleResults()}
              {activeTab === "posts" && renderPostsResults()}
              {activeTab === "videos" && renderVideosResults()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
